import React from 'react';
import Checklist from '../../library/Checklist';
import { PHOTO_STAGES } from './utils';

/**
 * Picks the icon for the row tracking Biostar's verdict - a cross once it rejects,
 * a spinner while a request is in flight, a tick only once the photo has passed.
 * @param {string} stage Current dialog stage
 * @param {string} biostarError Biostar's rejection message, if it rejected the photo
 * @returns Icon class for the row
 */
function statusIcon(stage, biostarError) {
    if (biostarError) {
        return 'fas fa-times-circle';
    }

    if (stage === PHOTO_STAGES.UPLOADING || stage === PHOTO_STAGES.SUBMITTING) {
        return 'fas fa-cog fa-spin';
    }

    if (stage === PHOTO_STAGES.PREVIEW) {
        return 'fas fa-check-circle';
    }

    return 'fal fa-circle';
}

/**
 * Picks the icon for the format/size rows. Both are false before a photo has been
 * checked, which is indistinguishable from failing - so a row only turns into a red
 * cross once there's a photo for it to be about.
 * @param {boolean} passed Whether the check passed
 * @param {boolean} hasBeenChecked Whether a photo has been checked yet
 * @returns Icon class for the row
 */
function localCheckIcon(passed, hasBeenChecked) {
    if (passed) {
        return 'fas fa-check-circle';
    }

    return hasBeenChecked ? 'fas fa-times-circle' : 'fal fa-circle';
}

/**
 * Checklist shown alongside the photo capture/preview panel. Format and size are
 * checked locally as soon as a photo lands; the third row is Biostar's own verdict,
 * which only resolves once the upload request returns.
 */
function PhotoChecklist({ localChecks, biostarError, stage }) {
    const hasBeenChecked = stage === PHOTO_STAGES.PREVIEW || stage === PHOTO_STAGES.SUBMITTING;

    const items = [
        {
            id: 'format',
            label: 'Image Format',
            description: 'Image must be JPEG or PNG',
            icon: localCheckIcon(localChecks.format, hasBeenChecked),
            done: localChecks.format,
            error: hasBeenChecked && !localChecks.format,
        },
        {
            id: 'size',
            label: 'Size',
            description: 'Is up to 10MB and face is 40-80cm from camera',
            icon: localCheckIcon(localChecks.size, hasBeenChecked),
            done: localChecks.size,
            error: hasBeenChecked && !localChecks.size,
        },
        {
            id: 'biostarVerdict',
            label: biostarError ? 'Photo Check' : 'Facial Expression',
            description: biostarError || 'Keep a neutral look with your mouth closed and eyes open; do not smile.',
            icon: statusIcon(stage, biostarError),
            done: stage === PHOTO_STAGES.PREVIEW && !biostarError,
            error: !!biostarError,
        },
    ];

    return <Checklist items={items} />;
}

export default PhotoChecklist;
